import React, { useState, useEffect, useMemo } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import './App.css';
import { 
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  RadialLinearScale,
  Filler
} from 'chart.js';
import { Bar, Doughnut, Line, Radar } from 'react-chartjs-2';
import { FaSortUp, FaSortDown, FaSort, FaUser, FaChartLine, FaPercentage, FaMoneyBillWave, FaBuilding, FaChevronLeft, FaCog, FaSignOutAlt, FaSpinner, FaUsers } from 'react-icons/fa';
import Login from './Login';
import SidePanel from './components/SidePanel';
import Settings from './components/Settings';
import { ThemeProvider, useTheme } from './context/ThemeContext';
import TeamsAnalytics from './components/TeamsAnalytics';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  RadialLinearScale,
  Filler
);

// Protected Route component
const ProtectedRoute = ({ children }) => {
  const isAuthenticated = !!localStorage.getItem('accessToken');
  return isAuthenticated ? children : <Navigate to="/login" />;
};

// Dashboard component
const Dashboard = () => {
  const { theme } = useTheme();
  const [salesAgents, setSalesAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMessage, setLoadingMessage] = useState("Loading data...");
  const [error, setError] = useState(null);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [agentDetails, setAgentDetails] = useState([]);
  const [agentOpportunityDetails, setAgentOpportunityDetails] = useState(null);
  const [showSidebar, setShowSidebar] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: 'closing_percentage', direction: 'desc' });
  const [activeTab, setActiveTab] = useState('performance');
  const [costCardExpanded, setCostCardExpanded] = useState(false);
  const [providersCardExpanded, setProvidersCardExpanded] = useState(false);
  const [allLeadSources, setAllLeadSources] = useState([]);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [mainTab, setMainTab] = useState('dashboard');
  const [dateRange, setDateRange] = useState({
    startDate: new Date(new Date().setDate(new Date().getDate() - 21)).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0]
  });
  const [isSidePanelOpen, setIsSidePanelOpen] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [excludedAgents, setExcludedAgents] = useState(() => {
    const saved = localStorage.getItem('excludedAgents');
    return saved ? JSON.parse(saved) : [];
  });

  // Backend API URL
  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

  // Chart options
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          color: theme.darkMode ? '#e6e6e6' : '#4a4a4a',
          font: {
            size: theme.fontSize === 'small' ? 12 : theme.fontSize === 'large' ? 16 : 14
          }
        }
      },
      title: {
        display: false,
      },
    },
    scales: {
      x: {
        ticks: {
          color: theme.darkMode ? '#b3b3b3' : '#65676b',
          font: {
            size: theme.fontSize === 'small' ? 10 : theme.fontSize === 'large' ? 14 : 12
          }
        },
        grid: {
          color: theme.darkMode ? '#404040' : '#e4e6eb'
        }
      },
      y: {
        ticks: {
          color: theme.darkMode ? '#b3b3b3' : '#65676b',
          font: {
            size: theme.fontSize === 'small' ? 10 : theme.fontSize === 'large' ? 14 : 12
          }
        },
        grid: {
          color: theme.darkMode ? '#404040' : '#e4e6eb'
        }
      }
    }
  };

  // Auth token check
  useEffect(() => {
    // Check for existing token on startup
    const token = localStorage.getItem('accessToken');
    if (token) {
      setIsAuthenticated(true);
    }
  }, []);

  // Auth fetch wrapper
  const fetchWithAuth = async (url, options = {}) => {
    const token = localStorage.getItem('accessToken');
    if (!token) {
      throw new Error('No authentication token found');
    }

    const headers = {
      ...options.headers,
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
    
    try {
      const response = await fetch(url, {
        ...options,
        headers
      });
      
      if (response.status === 401) {
        // Token expired or invalid
        localStorage.removeItem('accessToken');
        setIsAuthenticated(false);
        throw new Error('Authentication required');
      }
      
      return response;
    } catch (error) {
      console.error('API request failed:', error);
      throw error;
    }
  };

  useEffect(() => {
    // Fetch sales agents data only if authenticated
    const fetchSalesAgents = async () => {
      if (!isAuthenticated) return;

      try {
        setLoading(true);
        setLoadingMessage("Fetching agents list from external API...");
        
        // Step 1: Fetch agent names from the external API
        let agentNames = [];
        try {
          const agentsResponse = await fetch('https://csr-helper-07b80cdde45a.herokuapp.com/getAgents');
          
          if (!agentsResponse.ok) {
            throw new Error(`External API error! status: ${agentsResponse.status}`);
          }
          
          agentNames = await agentsResponse.json();
          
          if (!Array.isArray(agentNames) || agentNames.length === 0) {
            throw new Error('External API returned invalid or empty data');
          }
          
          setLoadingMessage(`Successfully fetched ${agentNames.length} agents. Loading KPI data...`);
        } catch (externalApiError) {
          setError(`Failed to fetch agents from external API: ${externalApiError.message}`);
          console.error('External API error:', externalApiError);
          setLoading(false);
          return; // Stop execution if we can't get the agent list
        }
        
        // Step 2: Get KPI data for these agents from our backend with auth
        const response = await fetchWithAuth(`${API_URL}/api/sales-agents?startDate=${dateRange.startDate}&endDate=${dateRange.endDate}`);
        
        if (!response.ok) {
          throw new Error(`Backend API error! status: ${response.status}`);
        }
        
        const kpiData = await response.json();
        
        // Step 3: Filter KPI data to only include agents from the external API
        const filteredAgents = kpiData.filter(agent => 
          agentNames.includes(agent.sales_agent)
        );
        
        if (filteredAgents.length === 0) {
          console.warn('No matching agents found between external API and KPI data');
        }
        
        setSalesAgents(filteredAgents);
        
        // Step 4: Fetch all agent details to get lead sources
        try {
          // Fetch details for all agents to aggregate lead sources
          const allAgentDetailsPromises = filteredAgents.map(async (agent) => {
            const detailsResponse = await fetchWithAuth(`${API_URL}/api/agent-details/${agent.sales_agent}?startDate=${dateRange.startDate}&endDate=${dateRange.endDate}`);
            if (!detailsResponse.ok) {
              throw new Error(`Failed to fetch details for ${agent.sales_agent}`);
            }
            return detailsResponse.json();
          });
          
          // Wait for all promises to resolve
          const allAgentDetailsData = await Promise.all(allAgentDetailsPromises);
          
          // Flatten all leads from all agents
          const allLeads = allAgentDetailsData.flat();
          
          // Extract unique sources
          const uniqueSources = [...new Set(allLeads.map(lead => lead.source))].filter(Boolean);
          
          setAllLeadSources(uniqueSources.sort());
        } catch (sourcesError) {
          console.error('Error aggregating lead sources:', sourcesError);
          // Use mock data for demonstration
          setAllLeadSources(['Top10', 'GoogleAds', 'Facebook', 'Referral', 'Direct', 'HomeAdvisor', 'Yelp']);
        }
      } catch (error) {
        setError(`Failed to fetch sales agents data: ${error.message}`);
        console.error('Error fetching sales agents data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchSalesAgents();
  }, [isAuthenticated, dateRange.startDate, dateRange.endDate]);

  // Get sorted agents
  const sortedAgents = useMemo(() => {
    let sortableAgents = [...salesAgents];
    if (sortConfig !== null) {
      sortableAgents.sort((a, b) => {
        let aValue, bValue;
        
        // Special handling for profit calculation
        if (sortConfig.key === 'total_binder_amount') {
          // Calculate profit for sorting (revenue - contacted leads cost)
          const aContactedCost = a.contacted_leads_cost || a.total_lead_cost;
          const bContactedCost = b.contacted_leads_cost || b.total_lead_cost;
          aValue = a.total_binder_amount - aContactedCost;
          bValue = b.total_binder_amount - bContactedCost;
        } else {
          aValue = a[sortConfig.key];
          bValue = b[sortConfig.key];
        }
        
        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableAgents;
  }, [salesAgents, sortConfig]);

  // Handle sorting
  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Get sort icon
  const getSortIcon = (columnName) => {
    if (sortConfig.key === columnName) {
      return sortConfig.direction === 'asc' ? <FaSortUp /> : <FaSortDown />;
    }
    return <FaSort />;
  };

  // Generate theme-aware colors
  const generateThemeColors = (count = 5) => {
    const baseColors = [theme.primaryColor, theme.secondaryColor];
    const colors = [];
    
    // Helper function to adjust color brightness
    const adjustBrightness = (color, amount) => {
      const hex = color.replace('#', '');
      const r = Math.max(0, Math.min(255, parseInt(hex.substr(0, 2), 16) + amount));
      const g = Math.max(0, Math.min(255, parseInt(hex.substr(2, 2), 16) + amount));
      const b = Math.max(0, Math.min(255, parseInt(hex.substr(4, 2), 16) + amount));
      return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
    };

    // Helper function to convert hex to rgba
    const hexToRgba = (hex, alpha = 1) => {
      const r = parseInt(hex.slice(1, 3), 16);
      const g = parseInt(hex.slice(3, 5), 16);
      const b = parseInt(hex.slice(5, 7), 16);
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    };

    // Generate colors based on primary and secondary colors
    for (let i = 0; i < count; i++) {
      if (i === 0) {
        colors.push(theme.primaryColor);
      } else if (i === 1) {
        colors.push(theme.secondaryColor);
      } else {
        // Create variations by adjusting brightness
        const baseColor = i % 2 === 0 ? theme.primaryColor : theme.secondaryColor;
        const brightness = i <= 2 ? 30 : i <= 4 ? -30 : (i % 2 === 0 ? 60 : -60);
        colors.push(adjustBrightness(baseColor, brightness));
      }
    }

    return {
      solid: colors,
      rgba: colors.map(color => hexToRgba(color, 0.6)),
      light: colors.map(color => hexToRgba(color, 0.3)),
      border: colors
    };
  };

  const handleAgentClick = async (agent) => {
    setSelectedAgent(agent);
    setShowSidebar(true);

    try {
      // Fetch regular agent details
      const response = await fetchWithAuth(`${API_URL}/api/agent-details/${agent.sales_agent}?startDate=${dateRange.startDate}&endDate=${dateRange.endDate}`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      setAgentDetails(data);

      // Fetch opportunity calculation details
      const opportunityResponse = await fetchWithAuth(`${API_URL}/api/agent-opportunity-details/${agent.sales_agent}?startDate=${dateRange.startDate}&endDate=${dateRange.endDate}`);
      
      if (opportunityResponse.ok) {
        const opportunityData = await opportunityResponse.json();
        setAgentOpportunityDetails(opportunityData);
      }
    } catch (error) {
      console.error('Error fetching agent details:', error);
    }
  };

  const closeSidebar = () => {
    setShowSidebar(false);
    setSelectedAgent(null);
    setAgentOpportunityDetails(null);
  };

  // Helper function to determine if a job is contacted based on backend criteria
  const isContactedJob = (lead) => {
    // If the job is booked, it's always considered contacted regardless of binder amount
    if (lead.status === "Booked Deals") {
      return true;
    }
    
    const excludedCfValues = ['350', '450', '500', '538', '650', '769', '800', '846', '900', '1000', '1134', '1153', '1200', '1500', '1800', '0'];
    
    // Check if job meets contacted criteria
    const priorityMatches = ['1', '3', '4', '7'].includes(lead.priority);
    const addressesPopulated = lead.from_address && lead.from_address.trim() !== '' && 
                              lead.to_address && lead.to_address.trim() !== '';
    const cfNotExcluded = !excludedCfValues.includes(lead.cf);
    
    return (priorityMatches || addressesPopulated) && cfNotExcluded;
  };

  // Analytics Data
  const analyticsData = useMemo(() => {
    if (!salesAgents.length) return null;

    // Calculate total metrics for agents
    const totalBooked = salesAgents.reduce((sum, agent) => sum + agent.booked_count, 0);
    const totalContacted = salesAgents.reduce((sum, agent) => sum + agent.contacted_count, 0);
    const overallClosingPercentage = totalContacted ? (totalBooked / totalContacted) * 100 : 0;
    const totalOpportunity = salesAgents.reduce((sum, agent) => sum + agent.total_opportunity, 0);
    const totalBinderAmount = salesAgents.reduce((sum, agent) => sum + agent.total_binder_amount, 0);
    const overallOpportunityClosedPercentage = totalOpportunity ? (totalBinderAmount / totalOpportunity) * 100 : 0;
    const averageCostPerUser = salesAgents.reduce((sum, agent) => sum + agent.cost_per_user, 0) / salesAgents.length;
    const leadSourcesCount = allLeadSources.length;
    
    // Top 5 agents by closing percentage with more than 0 contacts
    const topPerformers = [...salesAgents]
      .filter(agent => agent.contacted_count > 0)
      .sort((a, b) => b.closing_percentage - a.closing_percentage)
      .slice(0, 5);

    // Get theme colors
    const colors = generateThemeColors(Math.max(topPerformers.length, 10));

    // Data for charts
    const barChartData = {
      labels: topPerformers.map(agent => agent.sales_agent),
      datasets: [
        {
          label: 'Closing Percentage',
          data: topPerformers.map(agent => agent.closing_percentage),
          backgroundColor: colors.solid[0],
          borderColor: colors.border[0],
          borderWidth: 1,
        },
      ],
    };

    const pieChartData = {
      labels: topPerformers.map(agent => agent.sales_agent),
      datasets: [
        {
          label: 'Booked Deals',
          data: topPerformers.map(agent => agent.booked_count),
          backgroundColor: colors.solid.slice(0, topPerformers.length),
          borderColor: theme.darkMode ? '#2d2d2d' : '#ffffff',
          borderWidth: 2,
        },
      ],
    };

    const allAgentsClosingRate = {
      labels: sortedAgents.slice(0, 10).map(agent => agent.sales_agent),
      datasets: [
        {
          label: 'Closing Rate (%)',
          data: sortedAgents.slice(0, 10).map(agent => agent.closing_percentage),
          backgroundColor: colors.rgba[0],
          borderColor: colors.border[0],
          borderWidth: 2,
          fill: true,
        },
      ],
    };

    const radarData = {
      labels: ['Closing %', 'Cost per User ($)', 'Booked', 'Contacted', 'Efficiency'],
      datasets: topPerformers.slice(0, 3).map((agent, index) => ({
        label: agent.sales_agent,
        data: [
          agent.closing_percentage,
          agent.cost_per_user,
          agent.booked_count,
          agent.contacted_count,
          agent.closing_percentage / agent.cost_per_user * 10, // Efficiency metric
        ],
        backgroundColor: colors.light[index],
        borderColor: colors.border[index],
        borderWidth: 2,
        pointBackgroundColor: colors.solid[index],
        pointBorderColor: colors.border[index],
        pointBorderWidth: 1,
      })),
    };

    return {
      totalBooked,
      totalContacted,
      overallClosingPercentage,
      totalOpportunity,
      totalBinderAmount,
      overallOpportunityClosedPercentage,
      averageCostPerUser,
      leadSourcesCount,
      leadSources: allLeadSources,
      topPerformers,
      barChartData,
      pieChartData,
      allAgentsClosingRate,
      radarData
    };
  }, [salesAgents, sortedAgents, allLeadSources, theme, generateThemeColors]);

  // Add this function near the top of the Dashboard component
  const getLeadSourceCost = (source) => {
    const LEAD_SOURCE_COSTS = {
      "Spotower": 190.00,
      "Prime tower": 190.00,
      "IQ Media": 160.00,
      "Top Ten": 110.00,
      "Best Relocation": 175.00,
      "Crispx Long Distance": 306.78,
      "Iron Anchors": 277.88,
      "Consumer Voice": 205.00
    };

    // Direct match
    if (source in LEAD_SOURCE_COSTS) {
      return LEAD_SOURCE_COSTS[source];
    }

    // Case-insensitive match
    const sourceLower = source.toLowerCase();
    
    // Check for keyword matches
    const LEAD_SOURCE_KEYWORDS = {
      "Spotower": ["spot", "tower", "spotower"],
      "Prime tower": ["prime", "tower", "prime tower"],
      "IQ Media": ["iq", "media", "iq media"],
      "Top Ten": ["top", "ten", "top ten", "top10"],
      "Best Relocation": ["best", "relocation", "best relocation"],
      "Crispx Long Distance": ["crispx", "long", "distance", "crispx long", "long distance"],
      "Iron Anchors": ["iron", "anchor", "anchors", "iron anchor"],
      "Consumer Voice": ["consumer", "voice", "consumer voice"]
    };

    for (const [provider, keywords] of Object.entries(LEAD_SOURCE_KEYWORDS)) {
      for (const keyword of keywords) {
        if (sourceLower.includes(keyword.toLowerCase())) {
          return LEAD_SOURCE_COSTS[provider];
        }
      }
    }

    return 100.00; // Default cost
  };

  const handleAgentExclude = (agentName, excluded) => {
    setExcludedAgents(prev => {
      const newExcluded = excluded 
        ? [...prev, agentName]
        : prev.filter(name => name !== agentName);
      localStorage.setItem('excludedAgents', JSON.stringify(newExcluded));
      return newExcluded;
    });
  };

  // Update the filtered agents calculation
  const filteredAgents = useMemo(() => {
    if (!salesAgents) return [];
    return salesAgents.filter(agent => !excludedAgents.includes(agent.sales_agent));
  }, [salesAgents, excludedAgents]);

  // Update the KPI calculations to use filteredAgents instead of salesAgents
  const totalBooked = useMemo(() => {
    return filteredAgents.reduce((sum, agent) => sum + agent.booked_count, 0);
  }, [filteredAgents]);

  const totalContacted = useMemo(() => {
    return filteredAgents.reduce((sum, agent) => sum + agent.contacted_count, 0);
  }, [filteredAgents]);

  const averageClosingRate = useMemo(() => {
    if (filteredAgents.length === 0) return 0;
    const totalRate = filteredAgents.reduce((sum, agent) => sum + agent.closing_percentage, 0);
    return totalRate / filteredAgents.length;
  }, [filteredAgents]);

  return (
    <div className="App">
      {!isAuthenticated ? (
        <Login onLoginSuccess={() => setIsAuthenticated(true)} />
      ) : (
        <>
          <header className="App-header">
            <div className="header-content">
              <div className="header-left">
                <h1><FaChartLine /> Sales KPI Dashboard</h1>
                <p>Monitor sales performance and metrics</p>
              </div>
              <div className="date-range-selector">
                <div className="date-inputs">
                  <div className="date-filter-label">Filter by Open Date:</div>
                  <div className="date-input-group">
                    <label htmlFor="startDate">From:</label>
                    <input 
                      type="date" 
                      id="startDate" 
                      value={dateRange.startDate}
                      onChange={(e) => setDateRange({...dateRange, startDate: e.target.value})}
                    />
                  </div>
                  <div className="date-input-group">
                    <label htmlFor="endDate">To:</label>
                    <input 
                      type="date" 
                      id="endDate" 
                      value={dateRange.endDate}
                      onChange={(e) => setDateRange({...dateRange, endDate: e.target.value})}
                      min={dateRange.startDate}
                    />
                  </div>
                </div>
              </div>
              <div className="header-actions">
                <button 
                  className="settings-button" 
                  onClick={() => setIsSettingsOpen(true)}
                >
                  <FaCog />
                </button>
                <button 
                  className="logout-button" 
                  onClick={() => {
                    localStorage.removeItem('accessToken');
                    setIsAuthenticated(false);
                  }}
                >
                  Logout
                </button>
              </div>
              <button 
                className="floating-menu-button" 
                onClick={() => setIsSidePanelOpen(true)}
              >
                <FaChevronLeft />
              </button>
            </div>
          </header>

          <main className="App-main">
            {loading ? (
              <div className="intense-loading-screen">
                <div className="loading-container">
                  <div className="loading-animation">
                    <div className="pulse-rings">
                      <div className="pulse-ring"></div>
                      <div className="pulse-ring"></div>
                      <div className="pulse-ring"></div>
                    </div>
                    <div className="loading-icon">
                      <FaChartLine />
                    </div>
                  </div>
                  <div className="loading-content">
                    <h2 className="loading-title">Processing KPI Data</h2>
                    <div className="loading-message">{loadingMessage}</div>
                    <div className="loading-progress">
                      <div className="progress-bar">
                        <div className="progress-fill"></div>
                      </div>
                      <div className="loading-dots">
                        <span></span>
                        <span></span>
                        <span></span>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="loading-background">
                  <div className="floating-shapes">
                    <div className="shape shape-1"></div>
                    <div className="shape shape-2"></div>
                    <div className="shape shape-3"></div>
                    <div className="shape shape-4"></div>
                    <div className="shape shape-5"></div>
                  </div>
                </div>
              </div>
            ) : error ? (
              <div className="error">{error}</div>
            ) : (
          <div className="dashboard-container">
            {/* Main Navigation Tabs */}
            <div className="main-navigation">
              <button 
                className={`main-nav-tab ${mainTab === 'dashboard' ? 'active' : ''}`} 
                onClick={() => setMainTab('dashboard')}
              >
                <FaChartLine /> Dashboard
              </button>
              <button 
                className={`main-nav-tab ${mainTab === 'teams' ? 'active' : ''}`} 
                onClick={() => setMainTab('teams')}
              >
                <FaUsers /> Teams Analytics
              </button>
            </div>

            {/* Dashboard Content */}
            {mainTab === 'dashboard' && (
              <>
                

                {/* Analytics Charts */}
                {analyticsData && (
                  <div className="analytics-charts">
                    <div className="chart-tabs">
                      <button 
                        className={activeTab === 'performance' ? 'active' : ''} 
                        onClick={() => setActiveTab('performance')}
                      >
                        Performance
                      </button>
                      <button 
                        className={activeTab === 'comparison' ? 'active' : ''} 
                        onClick={() => setActiveTab('comparison')}
                      >
                        Agent Comparison
                      </button>
                      <button 
                        className={activeTab === 'trends' ? 'active' : ''} 
                        onClick={() => setActiveTab('trends')}
                      >
                        Closing Trends
                      </button>
                    </div>

                    <div className="charts-container">
                      {activeTab === 'performance' && (
                        <>
                          <div className="chart-container">
                            <h3>Top Performers - Closing Rate</h3>
                            <div className="chart-wrapper">
                              <Bar options={chartOptions} data={analyticsData.barChartData} />
                            </div>
                          </div>
                          <div className="chart-container">
                            <h3>Booked Deals Distribution</h3>
                            <div className="chart-wrapper">
                              <Doughnut options={chartOptions} data={analyticsData.pieChartData} />
                            </div>
                          </div>
                        </>
                      )}

                      {activeTab === 'comparison' && (
                        <div className="chart-container large">
                          <h3>Top 3 Agent Performance Comparison</h3>
                          <div className="chart-wrapper">
                            <Radar options={chartOptions} data={analyticsData.radarData} />
                          </div>
                        </div>
                      )}

                      {activeTab === 'trends' && (
                        <div className="chart-container large">
                          <h3>Agent Closing Rate Comparison</h3>
                          <div className="chart-wrapper">
                            <Line options={chartOptions} data={analyticsData.allAgentsClosingRate} />
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* KPI Table */}
                <div className="kpi-table-container">
                  <h2>Agent Performance</h2>
                  <table className="kpi-table">
                    <thead>
                      <tr>
                        <th onClick={() => requestSort('sales_agent')}>
                          <div className="th-content">
                            Agent
                            {getSortIcon('sales_agent')}
                          </div>
                        </th>
                        <th onClick={() => requestSort('cost_per_user')}>
                          <div className="th-content">
                            Cost per Lead {getSortIcon('cost_per_user')}
                          </div>
                        </th>
                        <th onClick={() => requestSort('total_lead_cost')}>
                          <div className="th-content">
                            Total Cost {getSortIcon('total_lead_cost')}
                          </div>
                        </th>
                        <th onClick={() => requestSort('total_binder_amount')} title="Revenue from closed deals minus cost of contacted leads">
                          <div className="th-content">
                            Revenue vs Cost {getSortIcon('total_binder_amount')}
                          </div>
                        </th>
                        <th onClick={() => requestSort('total_opportunity')}>
                          <div className="th-content">
                            OPP. # Amount {getSortIcon('total_opportunity')}
                          </div>
                        </th>
                        <th onClick={() => requestSort('closing_percentage')}>
                          <div className="th-content">
                            Closing Percentage {getSortIcon('closing_percentage')}
                          </div>
                        </th>
                        <th onClick={() => requestSort('opportunity_closed_percentage')}>
                          <div className="th-content">
                            Opportunity Closed % {getSortIcon('opportunity_closed_percentage')}
                          </div>
                        </th>
                        <th onClick={() => requestSort('booked_count')}>
                          <div className="th-content">
                            Booked {getSortIcon('booked_count')}
                          </div>
                        </th>
                        <th onClick={() => requestSort('contacted_count')}>
                          <div className="th-content">
                            Contacted {getSortIcon('contacted_count')}
                          </div>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedAgents.map(agent => {
                        // Use contacted leads cost for more accurate profit calculation
                        const contactedLeadsCost = agent.contacted_leads_cost || agent.total_lead_cost;
                        const agentProfit = agent.total_binder_amount - contactedLeadsCost;
                        const isProfitPositive = agentProfit >= 0;
                        
                        return (
                          <tr 
                            key={agent.sales_agent}
                            onClick={() => handleAgentClick(agent)}
                            className={excludedAgents.includes(agent.sales_agent) ? 'excluded' : ''}
                          >
                            <td>{agent.sales_agent}</td>
                            <td>${agent.cost_per_user}</td>
                            <td>${agent.total_lead_cost}</td>
                            <td className={`profit-cell ${isProfitPositive ? 'profit-positive' : 'profit-negative'}`}>
                              ${agentProfit.toFixed(2)}
                              <small style={{display: 'block', fontSize: '0.75rem', opacity: 0.7}}>
                                Revenue: ${agent.total_binder_amount.toFixed(2)}
                              </small>
                            </td>
                            <td>${agent.total_opportunity.toFixed(2)}</td>
                            <td>
                              <div className="percentage-bar-container">
                                <div 
                                  className="percentage-bar"
                                  style={{ width: `${Math.min(agent.closing_percentage, 100)}%` }}
                                ></div>
                                <span>{agent.closing_percentage}%</span>
                              </div>
                            </td>
                            <td>
                              <div className="percentage-bar-container">
                                <div 
                                  className="percentage-bar"
                                  style={{ width: `${Math.min(agent.opportunity_closed_percentage, 100)}%` }}
                                ></div>
                                <span>{agent.opportunity_closed_percentage}%</span>
                              </div>
                            </td>
                            <td>{agent.booked_count}</td>
                            <td>{agent.contacted_count}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </>
            )}

            {/* Teams Analytics Content */}
            {mainTab === 'teams' && (
              <TeamsAnalytics />
            )}

            {/* Agent Details Modal */}
            {showSidebar && selectedAgent && (
              <div className={`agent-details-modal-overlay ${showSidebar ? 'open' : ''}`} onClick={(e) => {
                if (e.target === e.currentTarget) closeSidebar();
              }}>
                <div className="agent-details-modal">
                  <div className="modal-header">
                    <h2><FaUser /> {selectedAgent.sales_agent}</h2>
                    <button className="modal-close-button" onClick={closeSidebar}>&times;</button>
                </div>
                
                  <div className="modal-content">
                    <div className="modal-left-column">
                <div className="agent-profile">
                  <div className="profile-avatar">
                    {selectedAgent.sales_agent.substring(0, 2).toUpperCase()}
                  </div>
                  <div className="profile-role">Sales Agent</div>
                </div>

                <div className="agent-stats">
                        <div className="stat-card">
                          <div className="stat-label">Closing Percentage</div>
                          <div className="stat-value">{selectedAgent.closing_percentage}%</div>
                        </div>
                        <div className="stat-card">
                          <div className="stat-label">Opportunity Closed</div>
                          <div className="stat-value">{selectedAgent.opportunity_closed_percentage}%</div>
                        </div>
                        <div className="stat-card">
                          <div className="stat-label">Total Opportunity</div>
                          <div className="stat-value">${selectedAgent.total_opportunity}</div>
                        </div>
                        <div className="stat-card">
                          <div className="stat-label">Booked / Contacted</div>
                          <div className="stat-value">{selectedAgent.booked_count} / {selectedAgent.contacted_count}</div>
                        </div>
                      </div>

                      <div className="agent-performance-chart">
                        <h3>Closing Rate vs. Average</h3>
                        {analyticsData && (
                          <div className="performance-bars">
                            <div className="performance-bar-container">
                              <div className="bar-label">Agent Closing %</div>
                              <div className="bar-wrapper">
                                <div 
                                  className="bar agent-bar"
                                  style={{ width: `${Math.min(selectedAgent.closing_percentage, 100)}%` }}
                                ></div>
                                <span className="bar-value">{selectedAgent.closing_percentage}%</span>
                              </div>
                            </div>
                            <div className="performance-bar-container">
                              <div className="bar-label">Average Closing %</div>
                              <div className="bar-wrapper">
                                <div 
                                  className="bar average-bar"
                                  style={{ width: `${Math.min(analyticsData.overallClosingPercentage, 100)}%` }}
                                ></div>
                                <span className="bar-value">{analyticsData.overallClosingPercentage.toFixed(2)}%</span>
                              </div>
                            </div>
                            <div className="performance-bar-container">
                              <div className="bar-label">Agent Opportunity %</div>
                              <div className="bar-wrapper">
                                <div 
                                  className="bar agent-bar"
                                  style={{ width: `${Math.min(selectedAgent.opportunity_closed_percentage, 100)}%` }}
                                ></div>
                                <span className="bar-value">{selectedAgent.opportunity_closed_percentage}%</span>
                              </div>
                            </div>
                            <div className="performance-bar-container">
                              <div className="bar-label">Avg Opportunity %</div>
                              <div className="bar-wrapper">
                                <div 
                                  className="bar average-bar"
                                  style={{ width: `${Math.min(analyticsData.overallOpportunityClosedPercentage, 100)}%` }}
                                ></div>
                                <span className="bar-value">{analyticsData.overallOpportunityClosedPercentage.toFixed(2)}%</span>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>

                  <div className="collapsible-stat-card">
                    <div className="collapsible-header" onClick={() => setCostCardExpanded(!costCardExpanded)}>
                      <div>
                        <div className="stat-label">Cost per Lead</div>
                        <div className="stat-value">${selectedAgent.cost_per_user}</div>
                        <div className="stat-label">Total Cost</div>
                        <div className="stat-value">${selectedAgent.total_lead_cost}</div>
                      </div>
                      <div>{costCardExpanded ? '▲' : '▼'}</div>
                    </div>
                    <div className={`collapsible-content ${costCardExpanded ? 'expanded' : ''}`}>
                      <div className="agent-sources-chart">
                        <h3>Lead Sources Distribution & Costs</h3>
                        {selectedAgent.lead_sources_breakdown && Object.keys(selectedAgent.lead_sources_breakdown).length > 0 ? (
                          <>
                            <div className="chart-wrapper">
                              <Doughnut 
                                options={chartOptions}
                                data={{
                                  labels: Object.keys(selectedAgent.lead_sources_breakdown),
                                  datasets: [{
                                    label: 'Leads by Source',
                                    data: Object.values(selectedAgent.lead_sources_breakdown).map(source => source.count),
                                    backgroundColor: generateThemeColors(Object.keys(selectedAgent.lead_sources_breakdown).length).solid,
                                    borderColor: theme.darkMode ? '#2d2d2d' : '#ffffff',
                                    borderWidth: 2,
                                  }]
                                }}
                              />
                            </div>
                            <div className="lead-source-cost-table">
                              <table>
                                <thead>
                                  <tr>
                                    <th>Source</th>
                                    <th>Count</th>
                                    <th>Percentage</th>
                                    <th>Cost</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {Object.entries(selectedAgent.lead_sources_breakdown).map(([source, data], index) => (
                                    <tr key={index}>
                                      <td>{source}</td>
                                      <td>{data.count}</td>
                                      <td>{data.percentage}%</td>
                                      <td>${data.cost}</td>
                                    </tr>
                                  ))}
                                  <tr className="total-row">
                                    <td colSpan="3"><strong>Weighted Average Cost:</strong></td>
                                    <td><strong>${selectedAgent.cost_per_user}</strong></td>
                                  </tr>
                                </tbody>
                              </table>
                            </div>
                          </>
                        ) : agentDetails.length > 0 ? (
                          <div className="chart-wrapper">
                            <Doughnut 
                              options={chartOptions}
                              data={{
                                labels: [...new Set(agentDetails.map(lead => lead.source))],
                                datasets: [{
                                  label: 'Leads by Source',
                                  data: [...new Set(agentDetails.map(lead => lead.source))].map(
                                    source => agentDetails.filter(lead => lead.source === source).length
                                  ),
                                  backgroundColor: generateThemeColors([...new Set(agentDetails.map(lead => lead.source))].length).solid,
                                  borderColor: theme.darkMode ? '#2d2d2d' : '#ffffff',
                                  borderWidth: 2,
                                }]
                              }}
                            />
                          </div>
                        ) : (
                          <div>No lead source data available</div>
                        )}
                          </div>
                      </div>
                    </div>
                  </div>
                  
                    <div className="modal-right-column">
                      {/* Opportunity Calculation Details */}
                      {agentOpportunityDetails && (
                        <div className="opportunity-calculation-details">
                          <h3>Opportunity Calculation Breakdown</h3>
                          
                          <div className="calculation-summary">
                            <div className="calc-formula">
                              <strong>Formula:</strong> Total Binders ÷ Total Opportunity × 100
                  </div>
                            <div className="calc-result">
                              <strong>${agentOpportunityDetails.totals.total_binder_amount} ÷ ${agentOpportunityDetails.totals.total_opportunity} × 100 = {agentOpportunityDetails.totals.opportunity_closed_percentage}%</strong>
                  </div>
                </div>

                          <div className="opportunity-breakdown">
                            <h4>Total Opportunity Calculation: ${agentOpportunityDetails.totals.total_opportunity}</h4>
                            <div className="opportunity-components">
                              <div className="opportunity-component">
                                <div className="component-header">Booked Contacted Jobs ({agentOpportunityDetails.totals.booked_contacted_count})</div>
                                <div className="component-calc">
                                  ${agentOpportunityDetails.totals.booked_est_total} × {agentOpportunityDetails.totals.opportunity_factor} = ${agentOpportunityDetails.totals.booked_opportunity}
                        </div>
                      </div>
                              <div className="opportunity-component">
                                <div className="component-header">Non-Booked Contacted Jobs ({agentOpportunityDetails.totals.non_booked_contacted_count})</div>
                                <div className="component-calc">
                                  ${agentOpportunityDetails.totals.non_booked_est_total} × {agentOpportunityDetails.totals.opportunity_factor} = ${agentOpportunityDetails.totals.non_booked_opportunity}
                        </div>
                      </div>
                              <div className="opportunity-total">
                                <strong>Total: ${agentOpportunityDetails.totals.booked_opportunity} + ${agentOpportunityDetails.totals.non_booked_opportunity} = ${agentOpportunityDetails.totals.total_opportunity}</strong>
                    </div>
                            </div>
                </div>

                          <div className="calculation-tabs">
                            <h4>All Contacted Jobs Details ({agentOpportunityDetails.totals.contacted_jobs_count})</h4>
                            <div className="jobs-breakdown-table">
                              <table>
                                <thead>
                                  <tr>
                                    <th>Job #</th>
                                    <th>Client</th>
                                    <th>Status</th>
                                    <th>Est Total</th>
                                    <th>× 0.35</th>
                                    <th>Opportunity</th>
                                    <th>Binder Amount</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {agentOpportunityDetails.contacted_jobs.map((job, index) => (
                                    <tr key={index} className={job.status === "Booked Deals" ? "booked-job" : "contacted-job"}>
                                      <td>{job.job_no}</td>
                                      <td>{job.client_name}</td>
                                      <td>{job.status}</td>
                                      <td>${job.est_total.toFixed(2)}</td>
                                      <td>× 0.35</td>
                                      <td>${job.opportunity.toFixed(2)}</td>
                                      <td>${job.binder_amount.toFixed(2)}</td>
                                    </tr>
                                  ))}
                                  <tr className="totals-row">
                                    <td colSpan="3"><strong>TOTALS:</strong></td>
                                    <td><strong>${agentOpportunityDetails.totals.total_est_total}</strong></td>
                                    <td><strong>× 0.35</strong></td>
                                    <td><strong>${agentOpportunityDetails.totals.total_opportunity}</strong></td>
                                    <td><strong>${agentOpportunityDetails.totals.total_binder_amount}</strong></td>
                                  </tr>
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </div>
                      )}
                
                <div className="agent-radar-chart">
                  <h3>Agent Performance Profile</h3>
                  {analyticsData && (
                    <div className="chart-wrapper">
                      <Radar 
                        options={chartOptions} 
                        data={{
                          labels: ['Closing %', 'Cost per User ($)', 'Booked', 'Contacted', 'Efficiency'],
                          datasets: [{
                            label: selectedAgent.sales_agent,
                            data: [
                              selectedAgent.closing_percentage,
                              selectedAgent.cost_per_user,
                              selectedAgent.booked_count,
                              selectedAgent.contacted_count,
                              selectedAgent.closing_percentage / selectedAgent.cost_per_user * 10, // Efficiency metric
                            ],
                            backgroundColor: generateThemeColors(1).light[0],
                            borderColor: generateThemeColors(1).border[0],
                            borderWidth: 2,
                            pointBackgroundColor: generateThemeColors(1).solid[0],
                            pointBorderColor: generateThemeColors(1).border[0],
                            pointBorderWidth: 1,
                          }]
                        }} 
                      />
                    </div>
                  )}
                </div>

                      <div className="leads-section">
                <h3>Recent Leads ({dateRange.startDate} to {dateRange.endDate})</h3>
                <div className="leads-table-container">
                  <table className="leads-table">
                    <thead>
                      <tr>
                        <th>Job #</th>
                        <th>Open Date</th>
                        <th>Client</th>
                        <th>Status</th>
                        <th>Source</th>
                      </tr>
                    </thead>
                    <tbody>
                              {agentDetails.map(lead => {
                                const isBooked = lead.status === "Booked Deals";
                                const isContacted = isContactedJob(lead);
                                let rowClass = "";
                                
                                if (isBooked) {
                                  rowClass = "booked-row";
                                } else if (isContacted) {
                                  rowClass = "contacted-row";
                                }
                                
                                return (
                                  <tr key={lead._id} className={rowClass}>
                          <td>{lead.job_no}</td>
                          <td>{lead.open_date}</td>
                          <td>{lead.first_name} {lead.last_name}</td>
                          <td>{lead.status}</td>
                          <td>{lead.source}</td>
                        </tr>
                                );
                              })}
                      {agentDetails.length === 0 && (
                        <tr>
                          <td colSpan="5" className="no-data">No leads found for this agent</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </main>

          <footer className="App-footer">
            <p>Sales KPI Dashboard &copy; 2025 | Tech-Inspired Analytics</p>
          </footer>

          <SidePanel 
            isOpen={isSidePanelOpen} 
            onClose={() => setIsSidePanelOpen(false)}
            analyticsData={analyticsData}
            salesAgents={salesAgents}
          />

          <Settings 
            isOpen={isSettingsOpen}
            onClose={() => setIsSettingsOpen(false)}
            salesAgents={salesAgents}
            onAgentExclude={handleAgentExclude}
            analyticsData={analyticsData}
          />
        </>
      )}
    </div>
  );
};

function App() {
  return (
    <ThemeProvider>
      <Router>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
        </Routes>
      </Router>
    </ThemeProvider>
  );
}

export default App;
